import { useState, useEffect } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Truck, Plus, X } from "lucide-react";
import { ShippingNotice } from "@/components/CatalogView";

export default function ShippingSettingsCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/shipping-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;

  const setFree = (k, v) => setS({ ...s, free: { ...s.free, [k]: v } });
  const setTiers = (k, v) => setS({ ...s, tiers: { ...s.tiers, [k]: v } });
  const setTier = (i, k, v) => setTiers("tiers", s.tiers.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const removeTier = (i) => setTiers("tiers", s.tiers.tiers.filter((_, j) => j !== i));
  const addTier = () => setTiers("tiers", [...s.tiers.tiers, { label: "", amount: 0 }]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...s, tiers: { ...s.tiers, tiers: s.tiers.tiers.map((t) => ({ label: t.label, amount: Number(t.amount) || 0 })) } };
      const r = await api.put("/admin/shipping-settings", body);
      setS(r.data);
      toast.success("Shipping notice saved");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const lbl = "text-xs uppercase tracking-[0.1em] text-slate-500";
  return (
    <Card className="rounded-sm p-6" data-testid="shipping-settings-card">
      <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><Truck className="h-4 w-4" /> Shipping notice</h2>
      <p className="text-sm text-slate-500 mb-5">Shown at the top of every customer's price list. End user, Installer and Certified Contractor see the free-shipping banner; Distributor and Wholesale tiers see the rate table.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-900">Free-shipping banner</div>
          <div className="space-y-1"><Label className={lbl}>Eyebrow</Label><Input value={s.free.eyebrow} onChange={(e) => setFree("eyebrow", e.target.value)} className="rounded-sm" data-testid="ship-free-eyebrow" /></div>
          <div className="space-y-1"><Label className={lbl}>Headline</Label><Input value={s.free.title} onChange={(e) => setFree("title", e.target.value)} className="rounded-sm" data-testid="ship-free-title" /></div>
          <div className="space-y-1"><Label className={lbl}>Text</Label><Textarea rows={2} value={s.free.text} onChange={(e) => setFree("text", e.target.value)} className="rounded-sm text-sm" data-testid="ship-free-text" /></div>
        </div>
        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-900">Distributor & Wholesale rate table</div>
          <div className="space-y-1"><Label className={lbl}>Header</Label><Input value={s.tiers.title} onChange={(e) => setTiers("title", e.target.value)} className="rounded-sm" data-testid="ship-tiers-title" /></div>
          <div className="space-y-1">
            <Label className={lbl}>Rates</Label>
            {s.tiers.tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`ship-tier-row-${i}`}>
                <Input value={t.label} onChange={(e) => setTier(i, "label", e.target.value)} placeholder="Label" className="rounded-sm flex-1" data-testid={`ship-tier-label-${i}`} />
                <span className="text-slate-400">$</span>
                <Input type="number" min="0" step="1" value={t.amount} onChange={(e) => setTier(i, "amount", e.target.value)} className="rounded-sm w-24" data-testid={`ship-tier-amount-${i}`} />
                <button onClick={() => removeTier(i)} className="text-slate-400 hover:text-red-600" data-testid={`ship-tier-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="ghost" className="rounded-sm text-slate-600" onClick={addTier} data-testid="ship-tier-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add rate</Button>
          </div>
          <div className="space-y-1"><Label className={lbl}>Footnote</Label><Textarea rows={2} value={s.tiers.text} onChange={(e) => setTiers("text", e.target.value)} className="rounded-sm text-sm" data-testid="ship-tiers-text" /></div>
        </div>
      </div>

      <div className="mt-6">
        <div className={`${lbl} mb-2`}>Live preview</div>
        <ShippingNotice shipping={{ mode: "free", ...s.free }} />
        <ShippingNotice shipping={{ mode: "tiers", ...s.tiers }} />
      </div>
      <Button onClick={save} disabled={busy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-shipping-button">{busy ? "Saving…" : "Save shipping notice"}</Button>
    </Card>
  );
}
